import { BL_Warranty } from '@rabbit/bizproc/core';
import { DTTenant_Public, Warranty, WarrantyStatus } from '@rabbit/data/types';
import { getUnixTime } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  formatUnixTime,
  getClaimLimit,
  getCurrencyFormat,
  getIncreasedLabourRate,
} from '../../utils/helpers';
import { isWarrantyExpired } from '@rabbit/elements/shared-components';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { calcWarrantyRemaining } from '@rabbit/bizproc/react';
import { useContext } from 'react';
import { AppContext } from '@rabbit/app-context';
import { useGetMySagePersonas } from '@rabbit/data/portal';
import { calcWarrantyTimeLeft } from '@rabbit/bizproc/react';

import { formatDate } from '@rabbit/sage/utils/helpers';
export interface RecordType {
  label: string;
  value: string | Array<string> | boolean | number | undefined;
  ValueElement?: JSX.Element;
}

export type TenantType = 'carTenants' | 'tableTenants' | 'defaultTenants';

export default function useWarrantyTypes({
  warranty,
  type,
  tenantInfo,
  handleOpenModal,
}: {
  type: TenantType;
  warranty: Warranty;
  tenantInfo: DTTenant_Public['info'];
  handleOpenModal: () => void;
}): RecordType[] {
  const { t } = useTranslation();
  const { warrantorPersona } = useGetMySagePersonas();

  const CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT =
    t('CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT') === 'true';

  const remainingTime = calcWarrantyRemaining(warranty);
  const totalDuration = calcWarrantyTimeLeft(
    warranty.endDate,
    warranty.startDate
  );

  let labourRateValue: any[] | null = [];
  if (type === 'carTenants') {
    labourRateValue = getIncreasedLabourRate(
      warranty,
      tenantInfo?.currency || 'GBP'
    );
  }

  const { config } = useContext(AppContext);

  const CFG_WARRANTY_INFO_URL = t('CFG_WARRANTY_INFO_URL');
  const CFG_WARRANTY_INFORMATION_URL = t('CFG_WARRANTY_INFO_URL');
  const showVAT = t('CFG_COBRAND_REGISTRATIONS_SHOW_VAT') === 'true';

  switch (type) {
    case 'defaultTenants':
      return [
        {
          label: t('general.warrantyId'),
          value: warranty?.warrantyLink?.slice(0, 5).toUpperCase(),
        },
        {
          label: t('general.registrationDate'),
          value: warranty?.registeredTime
            ? formatUnixTime(warranty.registeredTime)
            : '-',
        },
        {
          label: t('general.startDate'),
          value: warranty?.startDate
            ? formatUnixTime(getUnixTime(warranty.startDate))
            : '-',
        },
        {
          label: t('general.expiryDate'),
          value:
            totalDuration?.amount && totalDuration?.amount >= 1188
              ? '-'
              : warranty?.endDate
              ? formatUnixTime(getUnixTime(warranty.endDate))
              : '-',
        },
        {
          label: t('general.term'),
          value: BL_Warranty.getWarrantyTerm(warranty),
        },
        {
          label: t('general.warrantyExpired'),
          value: warranty?.endDate ? isWarrantyExpired(warranty?.endDate) : '-',
        },
        ...(config.WARRANTIES.WARRANTY_TC_FROM_FIRESTORE
          ? [
              {
                label: t('general.warrantyTerms'),
                value: '',
                ValueElement: (
                  <div
                    onClick={handleOpenModal}
                    className="flex cursor-pointer items-center gap-1 text-base font-medium text-gray-700"
                  >
                    {t('general.view')}
                    <ArrowTopRightOnSquareIcon className="h-4 w-4 text-black" />
                  </div>
                ),
              },
            ]
          : []),

        ...(t('tenantLink') !== 'SUPAGARD'
          ? [
              {
                // Putting hardcoded value for Supagard
                label: t('general.goTo'),
                value: '',
                ValueElement: RenderGoToUrl(
                  CFG_WARRANTY_INFO_URL,
                  'Terms & Conditions'
                ),
              },
              {
                label: t('general.warrantySummary'),
                value: '',
                ValueElement: RenderGoToUrl(
                  CFG_WARRANTY_INFORMATION_URL,
                  t('general.view')
                ),
              },
            ]
          : []),
      ];
    case 'tableTenants':
      return [
        {
          label: t('general.id'),
          value: warranty?.warrantyLink?.slice(0, 5).toUpperCase(),
        },
        {
          label: t('general.servicePlanType'),
          value:
            warranty?.templateTitle +
              ((warranty?.decider?.stipulated as any)?.surfaceAreaClass
                .label === 'Standard'
                ? ' (Standard)'
                : '') || '-',
        },
        {
          label: t('general.registrationDate'),
          value: warranty?.registeredTime
            ? formatUnixTime(getUnixTime(warranty.registeredTime))
            : '-',
        },
        {
          label: t('general.startDate'),
          value: warranty?.startDate
            ? formatUnixTime(getUnixTime(warranty.startDate))
            : '-',
        },
        {
          label: t('general.expiryDate'),
          value: warranty?.endDate
            ? formatUnixTime(getUnixTime(warranty.endDate))
            : '-',
        },
        ...(warrantorPersona !== null
          ? [
              {
                label: t('general.purchasePrice'),
                value: warranty?.decider?.decided?.totalPrice
                  ? getCurrencyFormat(
                      warranty?.decider?.decided?.totalPrice as number,
                      tenantInfo?.currency as string
                    )
                  : '-',
              },
            ]
          : []),
        {
          label: t('general.servicePlanTerm'),
          value: warranty?.duration.amount + ' ' + warranty?.duration.division,
        },
        {
          label: t('general.termRemaining'),
          value: remainingTime?.amount + ' ' + remainingTime?.unit,
        },
        {
          label: t('general.termExpired'),
          value: new Date() > new Date(warranty?.endDate || '') ? 'Yes' : 'No',
        },
        {
          label: t('general.extendedServicePlan'),
          value: 'Yes',
        },
        {
          label: t('general.goTo'),
          value: '',
          ValueElement: RenderGoToUrl(
            CFG_WARRANTY_INFO_URL,
            t('general.servicePlanTermsAndConditions')
          ),
        },
        // {
        //   label: t('general.troubleshootingGuide'),
        //   value: '',
        //   ValueElement: RenderGoToUrl(
        //     CFG_WARRANTY_INFO_URL,
        //     t('general.troubleshootingGuide')
        //   ),
        // },
      ];
    case 'carTenants':
      return [
        {
          label: t('Warranty ID'),
          value: warranty?.warrantyLink?.slice(0, 5).toUpperCase(),
        },
        {
          label: t('Warranty plan'),
          value: warranty?.templateTitle ? warranty?.templateTitle : '-',
        },
        {
          label: t('Registration date'),
          value:
            warranty && warranty?.registeredTime > 0
              ? formatUnixTime(warranty.registeredTime)
              : '-',
        },
        {
          label: t('Warranty term'),
          value: warranty?.duration.amount + ' ' + warranty?.duration.division,
        },
        {
          label: t('Start date'),
          value: warranty?.startDate
            ? formatDate(new Date(warranty.startDate))
            : '-',
        },
        {
          label: t('Expiry date'),
          value:
            warranty?.status === WarrantyStatus.VOIDED
              ? 'Voided'
              : warranty?.endDate
              ? formatDate(new Date(warranty.endDate))
              : '-',
        },
        {
          label: t('Warranty remaining'),
          value:
            remainingTime?.unit !== 'expired'
              ? remainingTime?.amount + ' ' + remainingTime?.unit
              : 'N/a',
        },
        {
          label: t('Warranty expired'),
          value: warranty?.endDate ? isWarrantyExpired(warranty?.endDate) : '-',
        },
        {
          label: t('Claim limit'),
          value: warranty
            ? getClaimLimit(
                warranty,
                tenantInfo?.currency,
                CFG_COBRAND_REGISTRATIONS_SHOW_CLAIMLIMIT_VAT
              )
            : '-',
        },
        {
          label: t('Extended warranty'),
          value: 'Yes',
        },
        ...(t('tenantLink') !== 'WARRANTYIRELAND' &&
        Array.isArray(labourRateValue) &&
        labourRateValue.length > 0
          ? [
              {
                label: t('Labour rate'),
                value: labourRateValue[0],
              },
            ]
          : []),
        {
          label: t('Partner'),
          value: warranty?.retailerName || '-',
        },
        {
          label: t('Salesperson'),
          value: warranty?.salesRep || '-',
        },
        {
          label: t('Price'),
          value: warranty?.price?.amount
            ? getCurrencyFormat(
                warranty?.price?.amount as number,
                warranty?.price?.currency
              ) +
              (showVAT
                ? ` (${getCurrencyFormat(
                    warranty?.price?.amount * 1.2,
                    warranty?.price?.currency
                  )} incl. VAT)`
                : '')
            : '-',
        },
      ];
    default: {
      return [];
    }
  }
}

const RenderGoToUrl = (url: string, label: string) => {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1 text-base font-medium text-gray-700"
    >
      {label}
      <ArrowTopRightOnSquareIcon className="h-4 w-4 text-black" />
    </a>
  );
};
